import { SidePanel, TemplateEditorWrapper } from '@AMIEWEB/Common/TemplateEditor';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { placementCandidateContractSaga } from 'store/redux-store/placement-candidate-contract-doc-editor/saga';
import { selectPlacementContractTemplatesEditor } from 'store/redux-store/placement-candidate-contract-doc-editor/selector';
import {
  pContractDocEditorActions,
  pContractDocEditorReducer,
  pContractDocEditorSliceKey,
} from 'store/redux-store/placement-candidate-contract-doc-editor/slice';
import { makeStyles } from 'tss-react/mui';
import { PlacementDetailsStatus } from '../PlacementDetails/PlacementDetailsPage/PlacementDetailsStatus';
import { useReadOnly } from 'oidc/userRoles';
import { usePromiseTracker } from 'react-promise-tracker';
import {
  IContractDoc,
  isAmieWebContractHtmlEditorByStatus,
  opensInCandidateContractHtmlEditor,
} from 'store/redux-store/placement-candidate-contract-doc-editor/types';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectEditorPreferences } from 'oidc/UserDevicePreference/userPreference.selectors';
import { IContractTemplate } from 'store/redux-store/contract-template-editor/types';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';

const useStyles = makeStyles()(theme => ({
  title: {
    fontSize: 16,
    fontWeight: 450,
  },
  clickable: {
    color: theme.palette.system.navyBlue,
  },
  titleDivider: {
    padding: '0 3px',
    '&::before': {
      content: '"/"',
    },
  },
}));

export const DocEditor = () => {
  useInjectReducer({ key: pContractDocEditorSliceKey, reducer: pContractDocEditorReducer });
  useInjectSaga({ key: pContractDocEditorSliceKey, saga: placementCandidateContractSaga });
  const [sideView, setSideView] = React.useState<SidePanel>('contracts');

  const history = useNavigate();
  const { readOnly } = useReadOnly();
  const dispatch = useDispatch();
  const { placementId } = useParams<{ placementId: string }>();
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    placementContractDocEditor: { value: editorPreference },
    hasLoadedPreferences,
  } = useSelector(selectEditorPreferences);

  const {
    templates,
    selectedTemplate,
    catalogTemplates,
    mergeTokenList,
    miscellaneous: { forceModalCloseFlag },
    document,
    renderPdfView,
    renderPreviewScreen,
    pdfViewFromEditor,
    pendingTinyMceHtmlInsert,
  } = useSelector(selectPlacementContractTemplatesEditor);

  const { promiseInProgress: isLoading } = usePromiseTracker({
    area: 'placement-candidate-contract-templates',
    delay: 0,
  });

  const { promiseInProgress: previewIsLoading } = usePromiseTracker({
    area: 'placement-client-contract-templates-preview-document',
    delay: 0,
  });

  const { promiseInProgress: isCatalogLoading } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.contractCatalogTemplates,
    delay: 0,
  });

  const { promiseInProgress: isContractTemplateRowsLoading } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.contractTemplateRows,
    delay: 0,
  });

  const pageTitle = React.useMemo(
    () => (
      <div className={classes.title}>
        <span className={classes.clickable}>{t('templateEditor.placement.allPlacements')}</span>
        <span className={classes.titleDivider} />
        <span className={classes.clickable}>{t('templateEditor.placement.pid', { placementId })}</span>
        <span className={classes.titleDivider} />
        <>{t('templateEditor.placement.contractEditor')}</>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placementId],
  );

  const prePageTitleAddOns = React.useMemo(() => [<PlacementDetailsStatus key={'PlacementDetailsStatus'} />], []);

  /** Like Settings `['tokens','templates']`: second icon opens master Templates list; here `contracts` + `templates`. */
  const titleIcons = React.useMemo(
    () =>
      selectedTemplate && isAmieWebContractHtmlEditorByStatus(selectedTemplate)
        ? ['contracts', 'templates']
        : ['contracts'],
    [selectedTemplate],
  );

  const handleEdit = React.useCallback(() => {
    dispatch(pContractDocEditorActions.resetToDocView());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreview = React.useCallback((templateText: string) => {
    dispatch(
      pContractDocEditorActions.previewContractDoc({
        placementId: parseInt(placementId),
        templateText,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = (template: IContractDoc, isTemplateDirty: boolean) => {
    dispatch(
      pContractDocEditorActions.createContractDoc({
        placementId: parseInt(placementId),
        documentId: template.documentId,
        templateHtml: template.templateText,
        isDraftManualEdit: isTemplateDirty || template.isDraftManualEdit,
        preservedApiTemplateTextRtf: template.preservedApiTemplateTextRtf,
      }),
    );
  };

  const handleCatalogTemplatePick = React.useCallback(
    (template: IContractTemplate) => {
      if (!selectedTemplate?.documentId) return;
      dispatch(
        pContractDocEditorActions.fetchContractTemplateRows({
          placementId: parseInt(placementId, 10),
          contractId: Number(selectedTemplate.documentId),
          templateId: template.templateId,
        }),
      );
    },
    [dispatch, placementId, selectedTemplate],
  );

  const onPendingTinyMceHtmlInsertConsumed = React.useCallback(() => {
    dispatch(pContractDocEditorActions.setPendingTinyMceHtmlInsert(null));
  }, [dispatch]);

  const onTemplateClick = React.useCallback(
    (contract: IContractDoc | null) => dispatch(pContractDocEditorActions.business_OnContractDocClick({ contract })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onSave = React.useCallback(
    (template: IContractDoc) => {
      const documentId = Number(template.documentId);
      dispatch(
        pContractDocEditorActions.saveDraftTemplate({
          placementId: parseInt(placementId),
          documentId,
          templateHtml: template.templateText,
          preservedApiTemplateTextRtf: template.preservedApiTemplateTextRtf,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placementId],
  );

  const updateEditorWidthPreference = width => {
    dispatch(
      userDevicePreferenceActions.business_saveEditorWidthPreference({
        editor: 'placementContractDocEditor',
        width,
      }),
    );
  };

  React.useEffect(() => {
    dispatch(pContractDocEditorActions.onInitAction({ placementId: parseInt(placementId) }));

    return () => {
      dispatch(pContractDocEditorActions.reset());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TemplateEditorWrapper
      //@ts-ignore
      templates={templates}
      //@ts-ignore
      selectedTemplate={selectedTemplate}
      onTemplateClick={onTemplateClick}
      onSave={onSave}
      mergeTokenList={mergeTokenList}
      pageTitle={pageTitle}
      prePageTitleAddOns={prePageTitleAddOns}
      isLoading={!hasLoadedPreferences}
      isLoadingSideView={isLoading}
      forceModalCloseFlag={forceModalCloseFlag}
      userIsReadOnly={readOnly}
      restrictToMergeEngine
      history={history}
      placementId={parseInt(placementId)}
      onCreate={handleCreate}
      onPreview={handlePreview}
      document={document}
      renderPreview={renderPreviewScreen}
      renderPdfView={renderPdfView}
      pdfViewFromEditor={pdfViewFromEditor}
      onEdit={handleEdit}
      previewIsloading={previewIsLoading}
      titleIcons={titleIcons}
      documentType="candidate-contract"
      disablePreview={!selectedTemplate || !opensInCandidateContractHtmlEditor(selectedTemplate)}
      disableTokenInteractions
      sideViewProps={[sideView, setSideView]}
      editorPreference={editorPreference}
      onSplitPaneDragEnd={({ clientWidth }) => updateEditorWidthPreference(clientWidth)}
      catalogTemplates={catalogTemplates}
      isCatalogLoading={isCatalogLoading || isContractTemplateRowsLoading}
      onCatalogTemplatePick={handleCatalogTemplatePick}
      pendingTinyMceHtmlInsert={pendingTinyMceHtmlInsert}
      onPendingTinyMceHtmlInsertConsumed={onPendingTinyMceHtmlInsertConsumed}
    />
  );
};
